// src/components/Header/Header.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowDown } from 'lucide-react';
import './Header.css';

// Optimized Particle component
const Particle = React.memo(({ id, size, left, top, duration, delay, opacity }) => {
  const style = {
    width: `${size}px`,
    height: `${size}px`,
    left: `${left}%`,
    top: `${top}%`,
    opacity: opacity,
    animation: `floatParticle ${duration}s ease-in-out infinite`,
    animationDelay: `${delay}s`,
  };

  return <div className="particle" style={style} aria-hidden="true" />;
});

Particle.displayName = 'Particle';

const Header = () => {
  const [particles, setParticles] = useState([]);
  const [scrollY, setScrollY] = useState(0);
  const headerRef = useRef(null);
  const resizeTimeout = useRef(null);

  // Generate particle with random properties
  const generateParticle = (i) => ({
    id: `particle-${i}-${Math.random().toString(36).substr(2, 9)}`,
    size: Math.random() * 5 + 3, // 3-8px
    left: Math.random() * 100, // 0-100% of viewport width
    top: Math.random() * 100, // 0-100% of viewport height
    duration: Math.random() * 10 + 10, // 10-20s (faster than before)
    delay: Math.random() * 10, // 0-10s
    opacity: Math.random() * 0.5 + 0.1, // 0.1-0.6
  });

  // Initialize particles with responsive count
  const initParticles = useCallback(() => {
    const count = Math.min(Math.floor(window.innerWidth / 15), 100); // Cap at 100 particles
    setParticles(Array.from({ length: count }, (_, i) => generateParticle(i)));
  }, []);

  // Handle window resize with debounce
  const handleResize = useCallback(() => {
    if (resizeTimeout.current) {
      clearTimeout(resizeTimeout.current);
    }
    resizeTimeout.current = setTimeout(() => {
      initParticles();
    }, 200);
  }, [initParticles]);

  // Handle scroll for scroll indicator
  const handleScroll = useCallback(() => {
    setScrollY(window.scrollY);
  }, []);

  // Initialize on mount and handle resize
  useEffect(() => {
    initParticles();
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
      if (resizeTimeout.current) {
        clearTimeout(resizeTimeout.current);
      }
    };
  }, [initParticles, handleScroll, handleResize]);

  return (
    <header 
      ref={headerRef}
      id="home" 
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      role="banner"
      aria-label="Hero Section"
    >
      {/* Animated Gradient Background */}
      <div 
        className="absolute inset-0 z-0 bg-[var(--primary-dark)]"
        aria-hidden="true"
      >
        <div 
          className="absolute inset-0 bg-gradient-to-br from-[var(--primary-dark)] via-[var(--primary-mid)] to-[var(--primary-dark)]"
          style={{
            backgroundSize: '200% 200%',
            animation: 'gradientBG 12s ease infinite',
            willChange: 'background-position',
          }}
          aria-hidden="true"
        />
        
        {/* Depth Effect Overlay */}
        <div className="depth-overlay" />
        
        {/* Floating Particles */}
        <div className="particles-container">
          {particles.map((particle) => (
            <Particle key={particle.id} {...particle} />
          ))}
        </div>
      </div>
      
      {/* Content */}
      <div className="relative z-20 text-center px-4 max-w-6xl mx-auto py-20">
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-[4.5rem] font-bold mb-6 leading-tight">
          Smart Waste Management <br />
          <span className="relative inline-block">
            <span className="relative z-10">for a Cleaner </span>
            <span className="bg-gradient-to-r from-white to-[#a3e635] bg-clip-text text-transparent animate-float">
              India
            </span>
          </span>
        </h1>

        <p className="text-lg md:text-xl text-gray-200 max-w-3xl mx-auto mb-12 animate-fade-in">
          BinBuddy revolutionizes waste collection with real-time tracking, 
          route optimization, and smart bin monitoring across the nation.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up" style={{ '--delay': '0.3s' }}>
          <a 
            href="#features" 
            className="btn-primary px-8 py-4 text-lg font-semibold rounded-full shadow-lg transform transition-all duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            aria-label="Discover Features"
          >
            Discover Features
          </a>
          <a 
            href="#about" 
            className="px-8 py-4 text-lg font-semibold rounded-full border-2 border-white/30 hover:border-white/50 hover:bg-white/10 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white/50"
            aria-label="Learn More About BinBuddy"
          >
            Learn More
          </a>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div 
        className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-30 transition-opacity duration-300 ${
          scrollY > 300 ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
        aria-hidden="true"
      >
        <a 
          href="#about" 
          className="flex flex-col items-center gap-2 group"
          aria-label="Scroll down to learn more"
        >
          <span className="text-sm font-medium text-white/80 group-hover:text-white transition-colors">
            Scroll Down
          </span>
          <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/20 transition-all">
            <ArrowDown className="w-5 h-5 animate-bounce text-white" />
          </div>
        </a>
      </div>
    </header>
  );
};

export default Header;
